import { useScrollReveal } from '@/hooks/useScrollReveal';

const services = [
  {
    icon: 'ri-search-eye-line',
    title: 'Executive Search',
    subtitle: 'C-Suite & Leadership Hiring',
    description: 'We identify and secure transformative leaders — CEOs, VPs, and board-level talent — who shape company trajectory. Deep market intelligence and a global network mean we find candidates others can\'t reach.',
    features: ['C-Level Search', 'Board Advisory', 'Succession Planning', 'Confidential Mandates'],
  },
  {
    icon: 'ri-group-line',
    title: 'Talent Acquisition',
    subtitle: 'Volume & Scale Hiring',
    description: 'When you need to scale fast — from 10 to 500 people — we deliver quality at speed. Structured sourcing, rigorous screening, and seamless onboarding support across every function and level.',
    features: ['Mass Recruitment', 'New Market Entry', 'Campus Hiring', 'Contract Staffing'],
  },
  {
    icon: 'ri-cpu-line',
    title: 'Technical & Niche Recruitment',
    subtitle: 'Specialist Talent Sourcing',
    description: 'Engineering, data science, life sciences, fintech — we speak your language. Our specialist recruiters understand the tech stacks, tools, and talent pools that drive innovation in your industry.',
    features: ['Engineering & IT', 'Data & Analytics', 'Life Sciences', 'Fintech & Finance'],
  },
  {
    icon: 'ri-organization-chart',
    title: 'Leadership Development',
    subtitle: 'Building Tomorrow\'s Leaders',
    description: 'Great leaders aren\'t born, they\'re built. We offer development programs designed to unlock leadership potential at every level of your organization.',
    features: ['Leadership Training', 'Coaching', 'Succession Planning', 'Culture Building'],
  },
];

const marqueeItems = [
  'Executive Search',
  'Talent Acquisition',
  'Technical & Niche Recruitment',
  'Leadership Development',
];

export default function ServicesSection() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.1);
  const { ref: gridRef, isVisible: gridVisible } = useScrollReveal(0.05);

  return (
    <section id="services" className="relative bg-foreground-900 py-24 md:py-32 overflow-hidden">
      {/* Glow blobs matching page 7 aesthetic */}
      <div className="absolute top-1/3 -right-40 w-[500px] h-[500px] rounded-full bg-accent-500/8 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 -left-40 w-[400px] h-[400px] rounded-full bg-primary-500/10 blur-3xl pointer-events-none"></div>

      {/* Scrolling services marquee */}
      <div className="relative w-full overflow-hidden mb-16 md:mb-20 border-y border-background-50/10 py-5 md:py-6">
        <div className="marquee-track flex whitespace-nowrap">
          {/* First set */}
          {marqueeItems.map((item, i) => (
            <div key={`a-${i}`} className="flex items-center gap-4 md:gap-6 mx-4 md:mx-8">
              <span className="font-heading text-xl md:text-3xl font-bold text-background-50/90 tracking-tight">
                {item}
              </span>
              <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-accent-500 flex-shrink-0"></span>
            </div>
          ))}
          {/* Second set (duplicate for seamless loop) */}
          {marqueeItems.map((item, i) => (
            <div key={`b-${i}`} className="flex items-center gap-4 md:gap-6 mx-4 md:mx-8">
              <span className="font-heading text-xl md:text-3xl font-bold text-background-50/90 tracking-tight">
                {item}
              </span>
              <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-accent-500 flex-shrink-0"></span>
            </div>
          ))}
          {/* Third set */}
          {marqueeItems.map((item, i) => (
            <div key={`c-${i}`} className="flex items-center gap-4 md:gap-6 mx-4 md:mx-8">
              <span className="font-heading text-xl md:text-3xl font-bold text-background-50/90 tracking-tight">
                {item}
              </span>
              <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-accent-500 flex-shrink-0"></span>
            </div>
          ))}
          {/* Fourth set */}
          {marqueeItems.map((item, i) => (
            <div key={`d-${i}`} className="flex items-center gap-4 md:gap-6 mx-4 md:mx-8">
              <span className="font-heading text-xl md:text-3xl font-bold text-background-50/90 tracking-tight">
                {item}
              </span>
              <span className="w-2 h-2 md:w-3 md:h-3 rounded-full bg-accent-500 flex-shrink-0"></span>
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto relative z-10">
        <div
          ref={headerRef}
          className={`mb-16 md:mb-20 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/15 text-accent-300 text-xs font-bold tracking-widest uppercase mb-5">
            Our Services
          </span>
          <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-[1.12] max-w-2xl">
            Recruitment{' '}
            <span className="text-accent-400 italic">That Delivers</span>
          </h2>
          <p className="mt-4 text-background-50/50 text-base md:text-lg max-w-xl leading-relaxed">
            Four pillars of recruitment expertise — from executive search to leadership development. We don&apos;t do HR generalism. We do recruitment, and only recruitment.
          </p>
        </div>

        {/* Auto-animated service cards */}
        <div
          ref={gridRef}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
        >
          {services.map((service, idx) => {
            const delay = idx * 150;
            return (
              <div
                key={idx}
                className={`group relative rounded-2xl bg-background-50/5 border border-background-50/10 hover:border-accent-500/30 p-6 md:p-8 transition-all duration-500 cursor-pointer hover:-translate-y-1.5 hover:bg-background-50/8 animate-float-card ${
                  gridVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{
                  transitionDelay: gridVisible ? `${delay}ms` : '0ms',
                  animationDelay: `${idx * 0.5}s`,
                }}
              >
                {/* Icon */}
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-accent-500/15 text-accent-400 mb-5 group-hover:scale-110 group-hover:bg-accent-500/25 transition-all duration-400">
                  <i className={`${service.icon} text-2xl`}></i>
                </div>

                <p className="text-background-50/35 text-xs font-semibold tracking-widest uppercase mb-1">
                  {service.subtitle}
                </p>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3 group-hover:text-accent-400 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-background-50/45 text-sm md:text-base leading-relaxed mb-6">
                  {service.description}
                </p>

                <div className="flex flex-wrap gap-2">
                  {service.features.map((feat) => (
                    <span
                      key={feat}
                      className="inline-block px-3 py-1.5 rounded-full bg-accent-500/10 text-accent-300 text-xs font-medium transition-all duration-300 group-hover:bg-accent-500/20 group-hover:scale-105"
                    >
                      {feat}
                    </span>
                  ))}
                </div>

                <div className="mt-6 pt-5 border-t border-background-50/8">
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-background-50/60 hover:text-accent-400 transition-colors cursor-pointer group/link"
                  >
                    Discuss your hiring needs
                    <span className="w-4 h-4 flex items-center justify-center group-hover/link:translate-x-1 transition-transform">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Marquee keyframes */}
      <style>{`
        @keyframes marqueeScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-track {
          animation: marqueeScroll 18s linear infinite;
        }
        @keyframes floatCard {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        .animate-float-card {
          animation: floatCard 4s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
}